// src/pages/ProjectPreviewPage.tsx
import React, { useEffect, useState, useRef } from 'react'
import { Folder, File, ArrowLeft, Sparkles, Download, Save, X } from 'lucide-react'
import { Link, useSearchParams } from 'react-router-dom'
import sdk, { type VM } from "@stackblitz/sdk"
import JSZip from 'jszip'
import SiteHeader from '@/components/SiteHeader'
import CodeUpdater from "@/components/CodeUpdater"
import HistoryDropdown from "@/components/HistoryDropdown"


// Tout en haut :
import MonacoEditor from "@monaco-editor/react"

const getLanguageFromPath = (path: string) => {
    if (!path) return "plaintext"
    if (path.endsWith(".js")) return "javascript"
    if (path.endsWith(".ts")) return "typescript"
    if (path.endsWith(".jsx")) return "javascript"
    if (path.endsWith(".tsx")) return "typescript"
    if (path.endsWith(".css")) return "css"
    if (path.endsWith(".scss")) return "scss"
    if (path.endsWith(".md")) return "markdown"
    if (path.endsWith(".json")) return "json"
    if (path.endsWith(".html")) return "html"
    if (path.endsWith(".yml") || path.endsWith(".yaml")) return "yaml"
    if (path.endsWith(".sh")) return "shell"
    if (path.endsWith(".txt")) return "plaintext"
    return "plaintext"
}

// --------------------------
// ARBORESCENCE
// --------------------------
function ProjectTree({ tree, indent = 0, onFileClick, selectedFile }) {
    return (
        <>
            {tree.map((node, i) => (
                <div key={i} className="relative">
                    {indent > 0 && (
                        <span
                            className="absolute left-0 top-0 h-full w-px bg-gray-200"
                            style={{ marginLeft: `${(indent - 1) * 20 + 10}px`, zIndex: 0 }}
                        />
                    )}
                    <div
                        style={{ marginLeft: `${indent * 20}px` }}
                        className={
                            "group flex items-center gap-2 py-1 px-2 rounded transition-colors cursor-pointer " +
                            (node.type === "file" && selectedFile === node.path
                                ? "bg-blue-100"
                                : "hover:bg-brixel/10 focus:bg-brixel/20"
                            )
                        }
                        tabIndex={0}
                        onClick={() => node.type === "file" && onFileClick?.(node.path)}
                    >
                        {node.type === "folder" ? (
                            <Folder className="w-4 h-4 text-brixel flex-none" />
                        ) : (
                            <File className="w-4 h-4 text-gray-400 flex-none" />
                        )}
                        <span
                            className={
                                "truncate " +
                                (node.type === "folder"
                                    ? "font-semibold text-brixel group-hover:text-brixel"
                                    : "text-gray-700 group-hover:text-blue-brixel")
                            }
                        >
                            {node.name}
                        </span>
                    </div>
                    {node.children && node.children.length > 0 && (
                        <ProjectTree
                            tree={node.children}
                            indent={indent + 1}
                            onFileClick={onFileClick}
                            selectedFile={selectedFile}
                        />
                    )}
                </div>
            ))}
        </>
    );
}

function buildTreeFromFileList(paths: string[]): any[] {
    paths = paths.sort();
    const root: any[] = [];
    const getOrCreate = (parent, name, type, path) => {
        let found = parent.find((n) => n.name === name && n.type === type);
        if (!found) {
            found = type === "folder"
                ? { type, name, children: [], path }
                : { type, name, path };
            parent.push(found);
        }
        return found;
    };
    for (const path of paths) {
        if (!path.trim()) continue;
        const parts = path.replace(/^\/|\/$/g, '').split('/');
        let parent = root;
        let subpath = '';
        for (let i = 0; i < parts.length; ++i) {
            subpath += (subpath ? '/' : '') + parts[i];
            const isLast = i === parts.length - 1;
            if (isLast && !path.endsWith('/')) {
                getOrCreate(parent, parts[i], "file", subpath);
            } else if (!isLast || path.endsWith('/')) {
                parent = getOrCreate(parent, parts[i], "folder", subpath).children;
            }
        }
    }
    function sortTree(tree: any[]) {
        tree.sort((a, b) => (a.type === 'folder' && b.type === 'file' ? -1 :
            a.type === 'file' && b.type === 'folder' ? 1 :
                a.name.localeCompare(b.name)));
        tree.forEach(node => node.children && sortTree(node.children));
    }
    sortTree(root);
    return root;
}

// --------------------------
// MAIN COMPONENT
// --------------------------
export default function ProjectPreviewPage() {
    const [tree, setTree] = useState<any[]>([])
    const [files, setFiles] = useState<Record<string, string>>({})
    const [error, setError] = useState<string | null>(null)
    const [selectedFile, setSelectedFile] = useState<string | null>(null)
    const [updatedArchiveURL, setUpdatedArchiveURL] = useState<string | null>(null)
    const [currentContent, setCurrentContent] = useState<string>("")
    const [showEditor, setShowEditor] = useState(false)
    const [history, setHistory] = useState<Array<{ archive_url: string, label: string }>>([]);

    // Stackblitz
    const [showStackblitz, setShowStackblitz] = useState(false)
    const [stackblitzLoading, setStackblitzLoading] = useState(false)
    const stackblitzRef = useRef<HTMLDivElement>(null)
    const stackblitzContainerRef = useRef<HTMLDivElement>(null);
    const stackblitzVm = useRef<VM | null>(null)
    const [stackblitzBooted, setStackblitzBooted] = useState(false)

    // Search param URL
    const [params] = useSearchParams()
    const archiveURL = params.get('url')


    // -- Extraction ZIP au chargement --
    useEffect(() => {
        if (!archiveURL) return;
        setError(null);
        setFiles({});
        setTree([]);
        setSelectedFile(null);
        setCurrentContent("");
        setShowEditor(false);
        setShowStackblitz(false);
        setStackblitzBooted(false);
        setHistory([{ archive_url: archiveURL, label: "Version 1" }]);

        (async () => {
            try {
                const res = await fetch(archiveURL)
                const blob = await res.blob()
                const arrayBuffer = await blob.arrayBuffer()
                const zip = await JSZip.loadAsync(arrayBuffer)
                const _files: Record<string, string> = {};
                const entries = Object.keys(zip.files).filter(name =>
                    !name.startsWith('__MACOSX/') &&
                    !name.includes('.DS_Store') &&
                    !name.startsWith('.git/')
                );
                setTree(buildTreeFromFileList(entries));
                await Promise.all(entries.map(async filename => {
                    const file = zip.files[filename];
                    if (!file.dir) {
                        _files[filename] = await file.async('text');
                    }
                }));
                setFiles(_files);
            } catch (err: any) {
                setError(err.message || 'Erreur lors du chargement du ZIP');
                setTree([]);
            }
        })();
    }, [archiveURL]);

    // -- Stackblitz: boot une seule fois --
    useEffect(() => {
        // check si la ref est bien attachée et qu'il y a des fichiers
        if (showStackblitz && stackblitzRef.current && Object.keys(files).length && !stackblitzBooted) {
            setStackblitzLoading(true);

            sdk.embedProject(
                stackblitzRef.current,
                {
                    files: files,
                    title: 'Aperçu React généré',
                    template: 'node',
                    description: '...',
                },
                {
                    view: "preview",
                    height: "100%",
                    width: "100%",
                    hideExplorer: true,
                    hideNavigation: true,
                    hideDevTools: true,
                }
            ).then((vm) => {
                stackblitzVm.current = vm;
                setStackblitzLoading(false);
                setStackblitzBooted(true);
            }).catch((err) => {
                setStackblitzLoading(false);
                setError("Erreur Stackblitz: " + err.message);
            });
        }
        // Indispensable: PAS stackblitzRef.current directement comme dépendance !
        // eslint-disable-next-line
    }, [showStackblitz, files, stackblitzBooted]);

    // --- Arborescence: sélection fichier à éditer
    function handleFileClick(filepath: string) {
        setSelectedFile(filepath)
        setCurrentContent(files[filepath])
        setShowEditor(true)
    }

    // --- Modification du textarea
    function handleFileEdit(val: string) {
        setCurrentContent(val)
    }

    // --- Sauvegarde: update state + stackblitz VM
    async function handleSave() {
        const path = selectedFile!;
        const newFiles = { ...files, [path]: currentContent }
        setFiles(newFiles);

        if (stackblitzVm.current) {
            await stackblitzVm.current.applyFsDiff({
                create: { [path]: currentContent },
                destroy: [],
                rename: []
            });
        }
        setShowEditor(false);
    }


    async function handleLoadArchiveVersion(url: string) {
        try {
            const res = await fetch(url);
            const blob = await res.blob();
            const arrayBuffer = await blob.arrayBuffer();
            const zip = await JSZip.loadAsync(arrayBuffer);
            const _files: Record<string, string> = {};
            const entries = Object.keys(zip.files).filter(name =>
                !name.startsWith('__MACOSX/') &&
                !name.includes('.DS_Store') &&
                !name.startsWith('.git/')
            );
            await Promise.all(entries.map(async filename => {
                const file = zip.files[filename];
                if (!file.dir) {
                    _files[filename] = await file.async('text');
                }
            }));

            setFiles(_files);
            setTree(buildTreeFromFileList(entries));
            setUpdatedArchiveURL(url);
            setSelectedFile(null);
            setCurrentContent("");
            setShowEditor(false);

            // Met à jour le contenu instantanément dans Stackblitz si ouvert
            if (stackblitzVm.current) {
                await stackblitzVm.current.applyFsDiff({
                    create: _files,
                    destroy: [], // (option: tu pourrais détruire les fichiers retirés... mais _files écrase tout)
                    rename: []
                });
            }
        } catch (err: any) {
            setError("Erreur lors du chargement de la version: " + (err.message || String(err)));
        }
    }

    // Fusionne les fichiers reçus dans l'état local & dans stackblitz
    async function handleCodeUpdaterChange(payload: { updated_files: Record<string, string>, new_files: Record<string, string>, archive_url: string }) {
        // Fusionne les deux objets
        const merged = { ...files, ...payload.updated_files, ...payload.new_files }
        setFiles(merged)
        setUpdatedArchiveURL(payload.archive_url)
        setHistory(prev =>
            [
                ...prev, // pour garder ordre croissant, ou inverse si tu préfères
                { archive_url: payload.archive_url, label: `Version ${prev.length + 1}` }
            ]
        );
        // Rebuild l'arborescence si nouveaux fichiers
        setTree(buildTreeFromFileList(Object.keys(merged)))

        // Pousse vers StackBlitz (si ouvert)
        if (stackblitzVm.current) {
            await stackblitzVm.current.applyFsDiff({
                create: { ...payload.updated_files, ...payload.new_files },
                destroy: [],
                rename: []
            })
        }
    }

    // --- Téléchargement du ZIP modifié
    async function handleDownloadZip() {
        const zip = new JSZip();
        Object.entries(files).forEach(([name, content]) => {
            zip.file(name, content);
        });
        const blob = await zip.generateAsync({ type: "blob" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = "mon-projet-modifie.zip";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    function handleBeforeMount(monaco) {
        if (!selectedFile) return
        const language = getLanguageFromPath(selectedFile)
        if (language === 'javascript') {
            monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
                noSyntaxValidation: true,
                noSemanticValidation: true,
            });
        }
        if (language === 'typescript') {
            monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
                noSyntaxValidation: true,
                noSemanticValidation: true,
            });
        }
        // Pour d'autres langages, à vérifier selon l'intégration
    }

    // --- Affichage éditeur modal
    const Editor = showEditor && selectedFile ? (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
            <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full h-[80vh] relative flex flex-col">
                <div className="flex flex-row items-center justify-between p-2 border-b bg-gray-50 rounded-t-2xl">
                    <span className="font-mono text-xs text-gray-500">{selectedFile}</span>
                    <button
                        onClick={() => setShowEditor(false)}
                        className="p-2 rounded hover:bg-gray-100"
                        title="Fermer"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="flex-1">
                    <MonacoEditor
                        value={currentContent}
                        language={getLanguageFromPath(selectedFile)}
                        theme="vs"
                        options={{
                            minimap: { enabled: false },
                            fontSize: 15,
                            scrollBeyondLastLine: false,
                            wordWrap: "on",
                            automaticLayout: true,
                            renderLineHighlight: "all",
                        }}
                        onChange={value => handleFileEdit(value ?? "")}
                        beforeMount={handleBeforeMount}
                        height="100%"
                    />
                </div>
                <div className="flex justify-end gap-2 p-2 border-t">
                    <button
                        className="flex items-center gap-1 px-4 py-2 text-white bg-brixel rounded font-semibold hover:bg-brixel/90"
                        onClick={handleSave}
                    >
                        <Save className="w-4 h-4" /> Sauvegarder
                    </button>
                </div>
            </div>
        </div>
    ) : null

    // -- Affichage classique
    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center text-red-500">{error}</div>
            </div>
        )
    }

    return (
        <main className="min-h-screen flex flex-col bg-white">
            {Editor}
            <div className="w-full px-6 py-4 border-b flex items-center bg-white">
                <SiteHeader />
                <div className="ml-auto">
                    <Link
                        to="/sitebuilder"
                        className="inline-flex items-center gap-2 text-brixel font-semibold hover:underline bg-white border border-brixel/20 shadow-sm rounded-lg px-4 py-2 transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5" />
                        Retour à l’éditeur
                    </Link>
                </div>
            </div>
            <div className="flex flex-col md:flex-row flex-1 w-full max-w-screen-2xl mx-auto px-4 py-8 gap-8">
                {/* Left - Arborescence */}
                <aside className="max-w-sm bg-gray-50 border-r flex flex-col rounded-xl h-[80vh] w-full md:w-1/3 mb-6 md:mb-0">
                    <div className="mb-4 px-4 pt-2">
                        <h2 className="text-lg font-bold text-brixel flex items-center gap-2">
                            <Folder className="w-5 h-5" /> Structure du projet
                        </h2>
                        <hr className="mt-2 border-brixel/20" />
                    </div>
                    <div className="flex-1 overflow-y-auto px-4 pb-4">
                        <div className="bg-white border border-gray-100 rounded-xl shadow-sm p-3">
                            <ProjectTree tree={tree} onFileClick={handleFileClick} selectedFile={selectedFile} />
                        </div>
                    </div>
                </aside>
                {/* Right - Preview */}
                <section className="flex-1 p-6 flex flex-col gap-4 bg-white rounded-xl shadow-sm border border-gray-100 w-full">
                    <div className="flex items-center justify-between mb-2">
                        <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                            <Sparkles className="w-5 h-5 text-brixel" /> Aperçu du site
                        </h2>
                        <CodeUpdater
                            apiUrl={`${import.meta.env.VITE_BACKEND_BASE_URL}/make_update`}
                            archiveUrl={updatedArchiveURL || archiveURL}
                            onUpdate={handleCodeUpdaterChange}
                            // Style discret:
                            className="ml-auto px-3 py-1 text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200 rounded transition-shadow shadow-none"
                            buttonText="Mettre à jour le code"
                        // 👆 À implémenter si besoin. Sinon wrapper le composant dans un bouton personnalisé!
                        />
                    </div>
                    {!showStackblitz && (
                        <div className="flex flex-1 min-h-[60vh] items-center justify-center">
                            <div className="flex flex-col items-center gap-4 w-full max-w-xs">
                                <button
                                    onClick={handleDownloadZip}
                                    className="inline-flex items-center justify-center gap-2 text-sm font-semibold border border-brixel/20 text-brixel hover:bg-brixel/10 rounded-lg px-5 py-2 transition shadow"
                                >
                                    <Download className="w-4 h-4" /> Télécharger l'archive
                                </button>
                                <button
                                    className="inline-flex items-center justify-center px-5 py-2 w-full bg-brixel text-white rounded-lg font-semibold shadow hover:bg-brixel/90 transition text-base"
                                    onClick={() => setShowStackblitz(true)}
                                    disabled={stackblitzLoading}
                                >
                                    <Sparkles className="w-4 h-4 mr-2" />
                                    Lancer la preview Stackblitz
                                </button>
                            </div>
                        </div>
                    )}
                    {showStackblitz && (
                        <div className="w-full min-h-[600px] flex flex-col items-stretch">
                            <div ref={stackblitzContainerRef} style={{ position: "relative", width: "100%", minHeight: 600 }}>
                                <button
                                    onClick={() => {
                                        if (stackblitzContainerRef.current?.requestFullscreen) {
                                            stackblitzContainerRef.current.requestFullscreen();
                                        }
                                    }}
                                    className="absolute top-2 right-2 z-20 bg-white/80 p-2 rounded shadow"
                                >
                                    🗖
                                </button>
                                <div ref={stackblitzRef} className="w-full min-h-[600px] rounded border" />
                            </div>
                            <div className="flex justify-center mt-6">
                                <button
                                    onClick={handleDownloadZip}
                                    className="inline-flex items-center justify-center gap-2 text-sm font-semibold border border-brixel/20 text-brixel hover:bg-brixel/10 rounded-lg px-5 py-2 transition shadow"
                                >
                                    <Download className="w-4 h-4" /> Télécharger l'archive
                                </button>
                                <HistoryDropdown
                                    history={history}
                                    currentArchiveUrl={updatedArchiveURL || archiveURL}
                                    onLoad={handleLoadArchiveVersion}
                                    onDownload={async url => {
                                        // Télécharger une ancienne version (re-fetch l’archive, pas besoin de JSZip)
                                        const res = await fetch(url);
                                        const blob = await res.blob();
                                        const a = document.createElement('a');
                                        a.href = URL.createObjectURL(blob);
                                        a.download = "archive-" + url.split('/').pop();
                                        document.body.appendChild(a);
                                        a.click();
                                        document.body.removeChild(a);
                                        URL.revokeObjectURL(a.href);
                                    }}
                                />
                            </div>
                        </div>
                    )}
                </section>
            </div>
        </main>
    )
}
